import { useState } from 'react';

import { RegistrationType } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/utils/cn';

import { EMPTY_TRANSFER, registrationOf, REGISTRATIONS, toAccountRequest, toDollars } from './account-model';
import { AccountCard, ExpectedValueField } from './AccountCard';
import { useAddPersonDialog } from './AddPeopleDialogs';
import { linkButtonClass, TextField } from './fields';
import { PersonKind } from './onboarding-model';
import { useCreateOnboardingAccount } from './onboarding-tab-queries';
import { OwnerPicker } from './OwnerPicker';
import { OnboardingHousehold } from './useOnboardingHousehold';

export const AccountsView = ({
    householdGUID,
    household,
}: {
    householdGUID: string;
    household: OnboardingHousehold;
}) => {
    const [open, setOpen] = useState<Record<string, boolean>>({});
    const [addingAccount, setAddingAccount] = useState(false);
    const { addPerson, dialog } = useAddPersonDialog(householdGUID, household);
    const setAll = (value: boolean) =>
        setOpen(Object.fromEntries(household.accounts.map((row) => [row.account.accountGUID, value])));
    return (
        <div className="flex flex-col gap-3">
            <div className="mb-1 flex items-center justify-between gap-4">
                <Button variant="outline" size="sm" onClick={() => setAddingAccount(true)}>
                    Add account
                </Button>
                <div className="flex gap-4">
                    <button type="button" className={linkButtonClass} onClick={() => setAll(true)}>
                        Expand all
                    </button>
                    <button type="button" className={linkButtonClass} onClick={() => setAll(false)}>
                        Collapse all
                    </button>
                </div>
            </div>
            {household.accounts.length === 0 && (
                <div className="border-border text-cocoa-400 rounded-lg border border-dashed px-5 py-4">
                    No onboarding accounts
                </div>
            )}
            {household.accounts.map((row) => (
                <AccountCard
                    // Kept across saves: a card takes up stored changes itself, so focus stays in its fields
                    key={row.account.accountGUID}
                    householdGUID={householdGUID}
                    household={household}
                    row={row}
                    open={!!open[row.account.accountGUID]}
                    onToggle={() =>
                        setOpen((current) => ({
                            ...current,
                            [row.account.accountGUID]: !current[row.account.accountGUID],
                        }))
                    }
                    addPerson={addPerson}
                />
            ))}
            {addingAccount && (
                <AddAccountDialog
                    householdGUID={householdGUID}
                    household={household}
                    onClose={() => setAddingAccount(false)}
                    addPerson={addPerson}
                />
            )}
            {dialog}
        </div>
    );
};

const Radio = ({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) => (
    <button
        type="button"
        role="radio"
        aria-checked={on}
        onClick={onClick}
        className={cn(
            'flex cursor-pointer items-center gap-2.5 rounded border px-3 py-2.5 text-left text-sm',
            on ? 'border-cocoa-900 bg-seafoam' : 'border-border bg-white',
        )}
    >
        <span
            className={cn(
                'size-3.5 flex-none rounded-full bg-white',
                on ? 'border-cocoa-900 border-4' : 'border-cocoa-300 border-[1.5px]',
            )}
        />
        {label}
    </button>
);

/**
 * Step 1 picks the registration, step 2 the owners and the account at another firm the money comes from; the backend
 * works out the title, the tax type and whether the money comes by transfer or rollover.
 */
const AddAccountDialog = ({
    householdGUID,
    household,
    onClose,
    addPerson,
}: {
    householdGUID: string;
    household: OnboardingHousehold;
    onClose: () => void;
    addPerson: (kind: PersonKind, onCreated: (personOrgGUID: string) => void) => void;
}) => {
    const [step, setStep] = useState<1 | 2>(1);
    const [type, setType] = useState<RegistrationType>();
    const [owners, setOwners] = useState<string[]>([]);
    const [contraFirm, setContraFirm] = useState('');
    const [contraAccountNumber, setContraAccountNumber] = useState('');
    const [value, setValue] = useState('');
    const create = useCreateOnboardingAccount(householdGUID);

    const registration = registrationOf(type);
    const isEntity = registration?.owner === 'ENTITY';
    const ownerCount = registration?.owner === 'JOINT' ? 2 : 1;
    const validValue = toDollars(value) !== undefined;
    const canCreate = !!type && owners.length === ownerCount && validValue && !create.isPending;

    const submit = () => {
        if (!type) return;
        create.mutate(
            toAccountRequest({
                registrationType: type,
                owners,
                expectedValue: value,
                contraFirm,
                contraAccountNumber,
                transfer: EMPTY_TRANSFER,
            }),
            { onSuccess: onClose },
        );
    };

    return (
        <Dialog open onOpenChange={(isOpen) => !isOpen && onClose()}>
            <DialogContent className="max-w-[520px]">
                <DialogHeader>
                    <DialogTitle className="font-heading text-[22px] font-normal">Add account</DialogTitle>
                    <DialogDescription>
                        {step === 1 ? 'Step 1 of 2 · Registration type' : `Step 2 of 2 · ${registration?.label}`}
                    </DialogDescription>
                </DialogHeader>
                {step === 1 ? (
                    <div role="radiogroup" aria-label="Registration type" className="grid grid-cols-2 gap-2">
                        {REGISTRATIONS.map((option) => (
                            <Radio
                                key={option.value}
                                on={type === option.value}
                                label={option.label}
                                onClick={() => {
                                    setType(option.value);
                                    setOwners([]);
                                }}
                            />
                        ))}
                    </div>
                ) : (
                    <>
                        <div className="flex flex-col gap-2.5">
                            <span className="text-cocoa-600 text-xs">
                                {isEntity ? 'Owner (entity)' : ownerCount === 2 ? 'Owners' : 'Owner'}
                            </span>
                            <OwnerPicker
                                shape={registration?.owner ?? 'PERSON'}
                                entityTypes={registration?.entityTypes}
                                owners={owners}
                                onChange={setOwners}
                                household={household}
                                addPerson={addPerson}
                            />
                            <span className="text-cocoa-400 text-xs">
                                {isEntity
                                    ? `Pick the ${type === RegistrationType.TRUST ? 'trust' : 'business'} from Clients`
                                    : ownerCount === 2
                                      ? 'Joint accounts need two owners'
                                      : 'Pick one person from Clients'}
                            </span>
                        </div>
                        <div className="bg-sand-100 flex flex-col gap-3.5 rounded-md p-3.5">
                            <span className="text-cocoa-600 text-xs">
                                Money from another firm (leave empty for a new account)
                            </span>
                            <TextField
                                label="Contra firm"
                                value={contraFirm}
                                onChange={setContraFirm}
                                placeholder="e.g. Morgan Stanley"
                            />
                            <TextField
                                label="Contra account number"
                                value={contraAccountNumber}
                                onChange={setContraAccountNumber}
                                placeholder="418-000000"
                            />
                            <ExpectedValueField value={value} onChange={setValue} />
                        </div>
                    </>
                )}
                <DialogFooter className="sm:justify-between">
                    {step === 2 ? (
                        <button
                            type="button"
                            className="text-gold-600 cursor-pointer text-sm font-medium"
                            onClick={() => setStep(1)}
                        >
                            ← Back
                        </button>
                    ) : (
                        <span />
                    )}
                    <div className="flex gap-2.5">
                        <Button variant="ghost" onClick={onClose}>
                            Cancel
                        </Button>
                        {step === 1 ? (
                            <Button disabled={!type} onClick={() => setStep(2)}>
                                Next
                            </Button>
                        ) : (
                            <Button disabled={!canCreate} onClick={submit}>
                                Create account
                            </Button>
                        )}
                    </div>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
